import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MainLayout from '../components/MainLayout';
import { api, ISubscription, IUserSubscription, ISubscriptionVariant } from '../api/api';
import usePageTitle from '../utils/usePageTitle';
import { useApiCall } from '../hooks/useApiCall';
import { useGlobalLoading } from '../hooks/useGlobalLoading';

// Стилизованные компоненты
const PageContainer = styled.div`
  padding: 20px 32px;
  width: 100%;
  
  @media (max-width: 1200px) {
    padding: 16px 24px;
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 40px;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 1024px) {
    margin-bottom: 32px;
    max-width: 480px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 40px;
  line-height: 1.2;
  color: var(--color-text);
  margin-bottom: 16px;
  letter-spacing: -0.04em;
  
  @media (max-width: 1024px) {
    font-size: 36px;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 8px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.6;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.7;
  }
`;

const TariffsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  justify-content: center;
  
  @media (max-width: 1200px) {
    max-width: 960px;
  }
  
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    max-width: 480px;
  }
  
  @media (max-width: 768px) {
    gap: 20px;
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
    max-width: 100%;
  }
`;

const TariffCard = styled.div<{ $isActive?: boolean; $position?: 'left' | 'middle' | 'right' }>`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.$isActive ? '#1B3332' : '#162A29'};
  border: 2px solid rgba(96, 159, 148, 0.16);
  flex: ${props => props.$isActive ? 1.2 : 1};
  min-width: ${props => props.$isActive ? 360 : 320}px;
  box-shadow: ${props => props.$isActive ? '0px 0px 64px rgba(0, 0, 0, 0.24)' : 'none'};
  z-index: ${props => props.$isActive ? 2 : 1};
  transform: ${props => props.$isActive ? 'scale(1.05)' : 'scale(1)'};
  margin-top: ${props => props.$isActive ? '-16px' : '0'};
  margin-bottom: ${props => props.$isActive ? '-16px' : '0'};
  transition: all 0.3s ease;
  
  ${props => props.$position === 'left' && `
    border-radius: 8px 0 0 8px;
    border-right: none;
  `}
  
  ${props => props.$position === 'middle' && `
    border-radius: 8px;
  `}
  
  ${props => props.$position === 'right' && `
    border-radius: 0 8px 8px 0;
    border-left: none;
  `}
  
  @media (max-width: 1200px) {
    min-width: ${props => props.$isActive ? 340 : 300}px;
  }
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: 480px;
    min-width: auto;
    border-radius: 8px !important;
    border: 2px solid rgba(96, 159, 148, 0.16) !important;
    transform: ${props => props.$isActive ? 'scale(1.02)' : 'scale(1)'};
    margin: ${props => props.$isActive ? '8px 0' : '0'};
  }
  
  @media (max-width: 768px) {
    max-width: 400px;
    transform: scale(1) !important;
    margin: 0 !important;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    min-height: auto;
  }
`;

const TariffHeader = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    gap: 4px;
  }
`;

const TariffName = styled.h2`
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 24px;
  line-height: 1.33;
  color: var(--color-text);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 1.4;
  }
`;

const TariffDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(96, 159, 148, 0.16);
  width: 100%;
`;

const TariffContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  
  @media (max-width: 768px) {
    gap: 24px;
  }
  
  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const Price = styled.span`
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 24px;
  line-height: 1.33;
  color: var(--color-text);
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const PricePeriod = styled.span`
  font-size: 16px;
  line-height: 1.5;
  color: var(--color-text-secondary);
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const FeatureText = styled.span`
  font-size: 14px;
  line-height: 1.43;
  color: var(--color-text-secondary);
`;

const ButtonContainer = styled.div<{ $isActive?: boolean }>`
  padding-top: 0;
  padding-bottom: 16px;
  padding-left: 24px;
  padding-right: 24px;
  margin-top: auto;
  
  @media (max-width: 768px) {
    padding-bottom: 12px;
    padding-left: 20px;
    padding-right: 20px;
  }
  
  @media (max-width: 480px) {
    padding-bottom: 10px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const Button = styled.button<{ $isPrimary?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 12px 0;
  border-radius: 9999px;
  border: none;
  background-color: ${props => props.$isPrimary ? 'var(--color-primary)' : '#2C4F4F'};
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.$isPrimary ? '#1FA36D' : '#355A5A'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Модальное окно
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #1B3332;
  border-radius: 16px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid rgba(96, 159, 148, 0.16);
  
  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
    width: 95%;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
    border-radius: 8px;
    width: 95%;
    max-width: none;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 24px;
  color: var(--color-text);
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 16px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const VariantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VariantCard = styled.div`
  background-color: #162A29;
  border: 2px solid rgba(96, 159, 148, 0.16);
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: var(--color-primary);
    background-color: #1B3332;
  }
`;

const VariantHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const VariantPeriod = styled.span`
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 18px;
  color: var(--color-text);
`;

const VariantPrice = styled.span`
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 20px;
  color: var(--color-primary);
`;

const VariantDescription = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 24px;
  border-radius: 9999px;
  border: none;
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  ${props => props.$variant === 'primary' ? `
    background-color: var(--color-primary);
    color: var(--color-text);
    
    &:hover {
      background-color: #1FA36D;
    }
  ` : `
    background-color: #2C4F4F;
    color: var(--color-text);
    
    &:hover {
      background-color: #355A5A;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Добавляю новый компонент для кнопки отписки
const UnsubscribeButton = styled.button`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  border-bottom: 1px dashed var(--color-text-secondary);
  cursor: pointer;
  margin-top: 24px;
  padding: 4px 0;
  opacity: 0.7;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    opacity: 1;
    color: var(--color-text);
    border-bottom-color: var(--color-text);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    margin-top: 20px;
    font-size: 13px;
    padding: 3px 0;
  }
  
  @media (max-width: 480px) {
    margin-top: 16px;
    font-size: 12px;
    padding: 2px 0;
  }
`;

// Компоненты для красивого уведомления
const SuccessNotification = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 32px;
  right: 32px;
  background: linear-gradient(135deg, #24B97D, #1FA36D);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(36, 185, 125, 0.3);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1001;
  transform: translateX(${props => props.$visible ? '0' : '100%'});
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: all 0.3s ease-in-out;
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 14px;
  
  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    left: 16px;
    transform: translateY(${props => props.$visible ? '0' : '-100%'});
  }
`;

const ErrorNotification = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 32px;
  right: 32px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(239, 68, 68, 0.3);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1001;
  transform: translateX(${props => props.$visible ? '0' : '100%'});
  opacity: ${props => props.$visible ? '1' : '0'};
  transition: all 0.3s ease-in-out;
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 14px;
  
  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    left: 16px;
    transform: translateY(${props => props.$visible ? '0' : '-100%'});
  }
`;

const SuccessIcon = styled.div`
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorIcon = styled.div`
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Компоненты для модального окна отписки
const UnsubscribeModalContent = styled(ModalContent)`
  max-width: 420px;
  text-align: center;
`;

const UnsubscribeIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const UnsubscribeModalTitle = styled(ModalTitle)`
  margin-bottom: 12px;
`;

const UnsubscribeModalText = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin: 0 0 32px 0;
`;

const UnsubscribeModalActions = styled(ModalActions)`
  gap: 12px;
`;

// Компоненты для бесплатного тарифа
const FreeTariffSection = styled.div`
  text-align: center;
  margin-top: 48px;
  margin-bottom: 40px;
  padding: 24px;
  border: 2px solid rgba(96, 159, 148, 0.16);
  border-radius: 8px;
  background-color: rgba(96, 159, 148, 0.05);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-top: 32px;
    margin-bottom: 32px;
    padding: 20px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    margin-top: 24px;
    margin-bottom: 24px;
    padding: 16px;
  }
`;

const FreeTariffTitle = styled.h3`
  color: var(--color-text);
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
    font-size: 16px;
  }
`;

const FreeTariffDescription = styled.p`
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.7;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

// Компонент уведомления об отключенном автопродлении
const AutoPayWarning = styled.div`
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1));
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    padding: 16px 20px;
    gap: 14px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
    padding: 14px 16px;
    gap: 12px;
    border-radius: 10px;
  }
`;

const WarningIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #ef4444;
  
  @media (max-width: 480px) {
    width: 22px;
    height: 22px;
  }
`;

const WarningText = styled.span`
  font-size: 16px;
  line-height: 1.5;
  color: var(--color-text);
  font-weight: 500;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const EnableAutoPayButton = styled.button`
  background: linear-gradient(135deg, var(--color-primary), #1FA36D);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, #1FA36D, #1C9162);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const TariffWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: 480px;
  }
  
  @media (max-width: 768px) {
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    margin-bottom: 32px;
  }
`;

// Компонент иконки галочки
const TickIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0003 18.3334C14.5837 18.3334 18.3337 14.5834 18.3337 10.0001C18.3337 5.41675 14.5837 1.66675 10.0003 1.66675C5.41699 1.66675 1.66699 5.41675 1.66699 10.0001C1.66699 14.5834 5.41699 18.3334 10.0003 18.3334Z" fill="#24B97D"/>
    <path d="M6.45801 9.99993L8.81634 12.3583L13.5413 7.6416" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Tariffs: React.FC = () => {
  usePageTitle('Тарифы');
  
  const { executeApiCall } = useApiCall();
  const { addLoadingStep, removeLoadingStep, updateLoadingStep } = useGlobalLoading();

  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [userSubscription, setUserSubscription] = useState<IUserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState<number | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<ISubscription | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [enableAutoPayLoading, setEnableAutoPayLoading] = useState(false);

  // Управляем глобальной загрузкой для основных данных
  useEffect(() => {
    if (loading) {
      addLoadingStep('tariffs-loading', 'Подбираем лучший тариф...', 5);
    } else {
      removeLoadingStep('tariffs-loading');
    }
  }, [loading, addLoadingStep, removeLoadingStep]);

  // Управляем глобальной загрузкой для покупки
  useEffect(() => {
    if (purchaseLoading !== null) {
      addLoadingStep('tariffs-purchase', 'Оформляем подписку...', 3);
    } else {
      removeLoadingStep('tariffs-purchase');
    }
  }, [purchaseLoading, addLoadingStep, removeLoadingStep]);

  // Управляем глобальной загрузкой для отписки
  useEffect(() => {
    if (unsubscribeLoading) {
      addLoadingStep('tariffs-unsubscribe', 'Отключаем автопродление...', 3);
    } else {
      removeLoadingStep('tariffs-unsubscribe');
    }
  }, [unsubscribeLoading, addLoadingStep, removeLoadingStep]);

  // Управляем глобальной загрузкой для включения автооплаты
  useEffect(() => {
    if (enableAutoPayLoading) {
      addLoadingStep('tariffs-autopay', 'Активируем Premium...', 3);
    } else {
      removeLoadingStep('tariffs-autopay');
    }
  }, [enableAutoPayLoading, addLoadingStep, removeLoadingStep]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subsResponse, userSubResponse] = await Promise.all([
        api.getSubscriptions(),
        api.getUserSubscription().catch(() => null)
      ]);
      
      setSubscriptions(subsResponse.subscriptions);
      setUserSubscription(userSubResponse);
    } catch (error) {
      // ошибки обрабатываются в executeApiCall
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (variant: ISubscriptionVariant) => {
    setPurchaseLoading(variant.id);
    const result = await executeApiCall(
      () => api.subscribe(variant.id),
      {
        errorMessage: 'Ошибка оформления подписки. Попробуйте позже.',
        showSuccessToast: false
      }
    );
    
    if (result && result.redirect_url) {
      window.location.href = result.redirect_url;
    }
    
    setPurchaseLoading(null);
  };

  const openUnsubscribeModal = () => {
    setShowUnsubscribeModal(true);
  };

  const closeUnsubscribeModal = () => {
    setShowUnsubscribeModal(false);
  };

  const handleUnsubscribe = async () => {
    setUnsubscribeLoading(true);
    const result = await executeApiCall(
      () => api.unsubscribe(),
      {
        successMessage: 'Автопродление подписки отключено',
        errorMessage: 'Ошибка отписки. Попробуйте позже.'
      }
    );
    
    if (result !== null) {
      // Обновляем локальное состояние - отключаем автопродление
      if (userSubscription) {
        setUserSubscription({
          ...userSubscription,
          is_auto_pay: false
        });
      }
      
      setShowUnsubscribeModal(false);
    }
    
    setUnsubscribeLoading(false);
  };

  const handleEnableAutoPay = async () => {
    setEnableAutoPayLoading(true);
    const result = await executeApiCall(
      () => api.enableAutoPay(),
      {
        successMessage: 'Автопродление подписки включено',
        errorMessage: 'Ошибка включения автопродления. Попробуйте позже.'
      }
    );
    
    if (result !== null) {
      // Обновляем локальное состояние
      setUserSubscription(result);
    }
    
    setEnableAutoPayLoading(false);
  };

  const openModal = (subscription: ISubscription) => {
    setSelectedSubscription(subscription);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSubscription(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getVariantPeriod = (variantIndex: number) => {
    const periods = ['1 месяц', '3 месяца', '12 месяцев'];
    return periods[variantIndex] || '1 месяц';
  };

  const getVariantDescription = (variantIndex: number, price: number) => {
    const descriptions = [
      'Базовый период подписки',
      'Экономия 15% при оплате сразу за 3 месяца',
      'Максимальная экономия 25% при оплате за год'
    ];
    return descriptions[variantIndex] || descriptions[0];
  };

  // Сортируем подписки так, чтобы активная была по середине (или PRO если нет подписки)
  const sortedSubscriptions = React.useMemo(() => {
    if (!subscriptions.length) return subscriptions;
    
    // Если нет подписки или подписка пустая, возвращаем исходный порядок (PRO в середине)
    if (!userSubscription || !userSubscription.subscription) {
      return subscriptions;
    }
    
    const activeIndex = subscriptions.findIndex(sub => sub.sub_name === userSubscription.subscription);
    if (activeIndex === -1) return subscriptions;
    
    const result = [...subscriptions];
    const activeSub = result.splice(activeIndex, 1)[0];
    const middleIndex = Math.floor(result.length / 2);
    result.splice(middleIndex, 0, activeSub);
    
    return result;
  }, [subscriptions, userSubscription]);

  // Если идет загрузка, показываем только лоадер
  if (loading) {
    return <MainLayout activePage="tariffs">{null}</MainLayout>;
  }

  return (
    <MainLayout activePage="tariffs">
      <PageContainer>
        {userSubscription && userSubscription.subscription && !userSubscription.is_auto_pay && (
          <AutoPayWarning>
            <WarningIcon>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </WarningIcon>
            <WarningText>
              Автопродление подписки отключено. Подписка завершится {formatDate(userSubscription.end_subscription_time)}
            </WarningText>
            <EnableAutoPayButton 
              onClick={handleEnableAutoPay}
              disabled={enableAutoPayLoading}
            >
              {enableAutoPayLoading ? 'Включение...' : 'Включить'}
            </EnableAutoPayButton>
          </AutoPayWarning>
        )}

        <HeaderSection>
          <Title>Тарифы сервиса</Title>
          <Description>
            {userSubscription ? 
              'Управляйте своей подпиской или переходите на другой тариф для получения дополнительных возможностей.' :
              'Переходите на платные тарифы, чтобы получить доступ к аналитике, интеграциям, расширенным отчётам и поддержке.'
            }
          </Description>
        </HeaderSection>

        {(!userSubscription || !userSubscription.subscription) && (
          <FreeTariffSection>
            <FreeTariffTitle>
              Бесплатный тариф
            </FreeTariffTitle>
            <FreeTariffDescription>
              Вы используете бесплатный тариф с базовыми возможностями. 
              Выберите подходящий платный тариф для получения расширенного функционала.
            </FreeTariffDescription>
          </FreeTariffSection>
        )}
        
        <TariffsContainer>
          {sortedSubscriptions.map((sub, index) => {
            // Если нет подписки, выделяем средний тариф (PRO)
            const isActive = userSubscription?.subscription 
              ? userSubscription.subscription === sub.sub_name 
              : index === Math.floor(sortedSubscriptions.length / 2);
            const position = index === 0 ? 'left' : index === sortedSubscriptions.length - 1 ? 'right' : 'middle';
            
            return (
              <TariffWrapper key={sub.sub_name}>
                <TariffCard 
                  $isActive={isActive} 
                  $position={position}
                >
                  <TariffHeader>
                    <TariffName>{sub.sub_name}</TariffName>
                    <TariffDescription>{sub.sub_access}</TariffDescription>
                  </TariffHeader>
                  
                  <Divider />
                  
                  <TariffContent>
                    <PriceSection>
                      <PriceContainer>
                        <Price>{sub.variants[0]?.price} ₽</Price>
                        <PricePeriod>в месяц</PricePeriod>
                      </PriceContainer>
                      
                      <FeaturesList>
                        {sub.sub_desc_list.map((feature, featureIndex) => (
                          <FeatureItem key={featureIndex}>
                            <TickIcon />
                            <FeatureText>{feature}</FeatureText>
                          </FeatureItem>
                        ))}
                      </FeaturesList>
                    </PriceSection>
                  </TariffContent>
                  
                  <ButtonContainer $isActive={isActive}>
                    {isActive && userSubscription?.subscription ? (
                      <Button>Ваш текущий тариф</Button>
                    ) : isActive && !userSubscription?.subscription ? (
                      <Button 
                        $isPrimary 
                        onClick={() => openModal(sub)}
                      >
                        Рекомендуемый тариф
                      </Button>
                    ) : (
                      <Button 
                        $isPrimary 
                        onClick={() => openModal(sub)}
                      >
                        Перейти на {sub.sub_name}
                      </Button>
                    )}
                  </ButtonContainer>
                </TariffCard>
                
                {isActive && userSubscription?.subscription && userSubscription?.is_auto_pay && (
                  <UnsubscribeButton onClick={openUnsubscribeModal}>
                    Отписаться
                  </UnsubscribeButton>
                )}
              </TariffWrapper>
            );
          })}
        </TariffsContainer>
      </PageContainer>

      {/* Модальное окно выбора периода */}
      {showModal && selectedSubscription && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Выберите период подписки</ModalTitle>
              <ModalSubtitle>Тариф {selectedSubscription.sub_name}</ModalSubtitle>
            </ModalHeader>
            
            <VariantsList>
              {selectedSubscription.variants.map((variant, index) => (
                <VariantCard 
                  key={variant.id} 
                  onClick={() => handleSubscribe(variant)}
                >
                  <VariantHeader>
                    <VariantPeriod>{getVariantPeriod(index)}</VariantPeriod>
                    <VariantPrice>{variant.price} ₽</VariantPrice>
                  </VariantHeader>
                  <VariantDescription>
                    {getVariantDescription(index, variant.price)}
                  </VariantDescription>
                </VariantCard>
              ))}
            </VariantsList>
            
            <ModalActions>
              <ModalButton $variant="secondary" onClick={closeModal}>
                Отмена
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
              )}

        {/* Модальное окно подтверждения отписки */}
        {showUnsubscribeModal && (
          <ModalOverlay onClick={closeUnsubscribeModal}>
            <UnsubscribeModalContent onClick={(e) => e.stopPropagation()}>
              <UnsubscribeIcon>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </UnsubscribeIcon>
              
              <UnsubscribeModalTitle>Отключить автопродление?</UnsubscribeModalTitle>
              <UnsubscribeModalText>
                Автопродление подписки будет отключено. Доступ к премиум-функциям сохранится до окончания текущего периода.
              </UnsubscribeModalText>
              
              <UnsubscribeModalActions>
                <ModalButton 
                  $variant="secondary" 
                  onClick={closeUnsubscribeModal}
                  disabled={unsubscribeLoading}
                >
                  Отмена
                </ModalButton>
                <ModalButton 
                  $variant="primary"
                  onClick={handleUnsubscribe}
                  disabled={unsubscribeLoading}
                  style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
                >
                  {unsubscribeLoading ? 'Отключение...' : 'Отключить'}
                </ModalButton>
              </UnsubscribeModalActions>
            </UnsubscribeModalContent>
          </ModalOverlay>
        )}
    </MainLayout>
  );
};

export default Tariffs; 